/** Настройки → Картинки (рисует Claude; Gemini — только с IMAGE_PROVIDER=gemini-api), Автоконтент (расписание подборки), Claude (модель и проверка). */
import { useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, ImagePlay, PlugZap, TriangleAlert } from "lucide-react";
import type { ClaudeCheck, ClaudeModels, GeminiCheck, PublicSettings } from "@impuls/shared";
import { api, errText } from "../lib/api";
import { duration, humanDateTime } from "../lib/format";
import { EcgLoader } from "./Ecg";
import { useElapsed } from "./JobFeed";
import { useConfirm, useToast } from "./ui";
import {
  SetBlock,
  SetFacts,
  SetField,
  SetKey,
  SetSaveBar,
  SetSection,
  setEnvHint,
  setHintId,
  useSetDirty,
  useSetDraft,
} from "./SettingsKit";

/* ================= Общая проверка сервиса ================= */
interface CheckState<T> {
  started: number | null;
  running: boolean;
  result: T | null;
  error: string;
}

function useServiceCheck<T>(url: string) {
  const [st, setSt] = useState<CheckState<T>>({ started: null, running: false, result: null, error: "" });
  const elapsed = useElapsed(st.started, st.running);
  const run = async () => {
    setSt({ started: Date.now(), running: true, result: null, error: "" });
    try {
      const r = await api.post<T>(url);
      setSt((x) => ({ ...x, running: false, result: r }));
      return r;
    } catch (e) {
      setSt((x) => ({ ...x, running: false, error: errText(e) }));
      return null;
    }
  };
  return { ...st, elapsed, run };
}

/* ================= Картинки ================= */
/** Понятные названия моделей Gemini; незнакомые показываем как есть */
const IMAGE_MODEL_LABELS: Record<string, string> = {
  "gemini-3-pro-image": "Nano Banana Pro — лучшее качество",
  "gemini-nano-banana-2.1": "Nano Banana 2.1 — быстрее и дешевле",
  "gemini-3.1-flash-image": "Nano Banana 2",
  "gemini-3.1-flash-lite-image": "Nano Banana 2 Lite — самая дешёвая",
};

export function SettingsImages({ s }: { s: PublicSettings }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const locked = new Set(s.env_locked);
  // Gemini — только если включён флагом в .env; по умолчанию картинки рисует Claude и Gemini не вызывается вовсе
  const gemini = s.image_provider === "gemini-api";
  const d = useSetDraft({ gemini_model: s.gemini_model, auto_generate_images: s.auto_generate_images });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useSetDirty("images", d.dirty);

  const models = useQuery({
    queryKey: ["gemini-models"],
    queryFn: () => api.get<{ models: string[]; error: string | null }>("/api/gemini/models"),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: gemini,
  });
  // порядок — как прислал сервер (Pro первой), выбранная модель всегда в списке
  const options = useMemo(
    () => [...new Set([...(models.data?.models ?? []), s.gemini_model, d.value.gemini_model])].filter(Boolean),
    [s.gemini_model, d.value.gemini_model, models.data],
  );

  const check = useServiceCheck<GeminiCheck>("/api/images/check");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(d.changes)) if (!locked.has(k)) body[k] = v;
    if (!Object.keys(body).length) return d.reset();
    setBusy(true);
    setError("");
    try {
      const r = await api.put<PublicSettings>("/api/settings", body);
      qc.setQueryData(["settings"], r);
      d.reset();
      toast("Настройки картинок сохранены");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const saveKey = async (key: string) => {
    const r = await api.put<PublicSettings>("/api/settings", { gemini_key: key });
    qc.setQueryData(["settings"], r);
    void qc.invalidateQueries({ queryKey: ["gemini-models"] });
    toast("Ключ Google AI Studio сохранён");
  };
  const removeKey = async () => {
    const ok = await confirm({
      title: "Удалить ключ Google AI Studio?",
      text: "Gemini перестанет рисовать картинки, пока вы не вставите новый ключ.",
      confirm: "Удалить ключ",
      danger: true,
    });
    if (!ok) return false;
    const r = await api.del<PublicSettings>("/api/settings/secret/gemini_key");
    qc.setQueryData(["settings"], r);
    void qc.invalidateQueries({ queryKey: ["gemini-models"] });
    toast("Ключ Google AI Studio удалён");
    return true;
  };

  const modelHint = locked.has("gemini_model")
    ? setEnvHint("gemini_model")
    : models.data?.error
      ? `Список моделей не загрузился: ${models.data.error}`
      : null;
  const res = check.result;
  return (
    <SetSection
      id="images"
      title="Картинки"
      status={
        !gemini ? (
          <span className="chip chip--ok">Рисует Claude</span>
        ) : (
          s.gemini_mock && <span className="chip chip--outline">Тестовый режим</span>
        )
      }
    >
      <SetBlock>
        {gemini && (
          <div className="set-cards set-cards--one">
            <SetKey
              id="gemini-key"
              title="Ключ Google AI Studio"
              isSet={s.gemini_key_set}
              mask={s.gemini_key}
              locked={locked.has("gemini_key")}
              envKey="gemini_key"
              placeholder="AIza…"
              onSave={saveKey}
              onRemove={removeKey}
              help={
                <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer">
                  Где взять ключ
                </a>
              }
            />
          </div>
        )}
        <form className="set-form" onSubmit={submit} noValidate>
          <div className="set-grid">
            {gemini && (
              <SetField id="gemini-model" label="Модель" hint={modelHint}>
                <select
                  id="gemini-model"
                  className="select"
                  value={d.value.gemini_model}
                  onChange={(e) => d.set("gemini_model", e.target.value)}
                  disabled={locked.has("gemini_model")}
                  aria-describedby={modelHint ? setHintId("gemini-model") : undefined}
                >
                  {options.map((m) => (
                    <option key={m} value={m}>
                      {IMAGE_MODEL_LABELS[m] ?? m}
                    </option>
                  ))}
                </select>
              </SetField>
            )}
            <div className="set-switch-cell">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={d.value.auto_generate_images}
                  onChange={(e) => d.set("auto_generate_images", e.target.checked)}
                />
                <span className="switch__track" aria-hidden="true" />
                <span>Картинки к автопостам</span>
              </label>
            </div>
          </div>
          <SetSaveBar
            dirty={d.dirty}
            busy={busy}
            error={error}
            onReset={() => {
              d.reset();
              setError("");
            }}
          />
        </form>
      </SetBlock>

      <SetBlock title="Проверка">
        <div className="set-actions">
          <button type="button" className="btn btn--dark" onClick={() => void check.run()} disabled={check.running || d.dirty}>
            <ImagePlay size={17} aria-hidden="true" />
            Тестовая картинка
          </button>
          {d.dirty && <span className="set-save__note">Сначала сохраните</span>}
        </div>
        {check.running && (
          <div className="set-live">
            <EcgLoader label={gemini ? "Gemini рисует картинку" : "Claude рисует иллюстрацию"} />
            <span className="set-live__time">{duration(check.elapsed)}</span>
          </div>
        )}
        {check.error && (
          <div className="error-line" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{check.error}</span>
          </div>
        )}
        {res && (
          <div className={`set-check set-check--${res.ok ? "ok" : "bad"}`} role="status">
            <p className="set-check__title">
              {res.ok ? <CircleCheck size={18} aria-hidden="true" /> : <CircleAlert size={18} aria-hidden="true" />}
              {res.ok ? `Картинка готова за ${duration(res.duration_ms)}` : `Картинка не получилась (${duration(res.duration_ms)})`}
            </p>
            {res.ok && res.image_url && (
              <a className="set-test-img" href={res.image_url} target="_blank" rel="noreferrer" title="Открыть в полном размере">
                <img src={res.image_url} alt="Тестовая картинка" width={280} height={210} />
              </a>
            )}
            <SetFacts items={[["Модель", res.mock ? "тестовый режим" : res.model]]} />
            {res.message && <p className="set-check__msg">{res.message}</p>}
          </div>
        )}
      </SetBlock>
    </SetSection>
  );
}

/* ================= Автоконтент ================= */
const TZ_HINTS = ["Asia/Yekaterinburg", "Europe/Moscow", "Europe/Samara", "Asia/Omsk", "Asia/Novosibirsk", "Asia/Krasnoyarsk", "UTC"];
const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;
const isTz = (tz: string) => {
  try {
    new Intl.DateTimeFormat("ru-RU", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

export function SettingsAuto({ s }: { s: PublicSettings }) {
  const toast = useToast();
  const qc = useQueryClient();
  const d = useSetDraft({
    auto_enabled: s.auto_enabled,
    auto_interval_days: String(s.auto_interval_days),
    auto_hour: String(s.auto_hour),
    timezone: s.timezone,
    auto_news_count: String(s.auto_news_count),
    auto_fact_count: String(s.auto_fact_count),
    auto_myth_count: String(s.auto_myth_count),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useSetDirty("auto", d.dirty);
  const v = d.value;

  const interval = Number(v.auto_interval_days);
  const news = Number(v.auto_news_count);
  const facts = Number(v.auto_fact_count);
  const myths = Number(v.auto_myth_count);
  const intOk = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max;

  const validate = (): string => {
    if (!intOk(interval, 1, 30)) return "Интервал — целое число дней от 1 до 30.";
    if (!intOk(news, 0, 5)) return "Новостей за запуск — от 0 до 5.";
    if (!intOk(facts, 0, 5)) return "Фактов за запуск — от 0 до 5.";
    if (!intOk(myths, 0, 5)) return "Мифов за запуск — от 0 до 5.";
    if (!v.timezone.trim() || !isTz(v.timezone.trim())) return "Неизвестный часовой пояс. Пример: Asia/Yekaterinburg.";
    return "";
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const bad = validate();
    if (bad) return setError(bad);
    const body: Record<string, unknown> = {};
    for (const k of Object.keys(d.changes)) {
      if (k === "auto_enabled") body[k] = v.auto_enabled;
      else if (k === "timezone") body[k] = v.timezone.trim();
      else body[k] = Number(v[k as keyof typeof v]);
    }
    setBusy(true);
    setError("");
    try {
      const r = await api.put<PublicSettings>("/api/settings", body);
      qc.setQueryData(["settings"], r);
      d.reset();
      void qc.invalidateQueries({ queryKey: ["auto-status"] });
      toast(r.auto_enabled ? "Расписание сохранено" : "Плановая подборка выключена");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const next = s.auto_next_run;
  const overdue = next !== null && next < Date.now();

  return (
    <SetSection
      id="auto"
      title="Автоконтент"
      status={
        s.auto_enabled ? (
          <span className="chip chip--ok">{next !== null && !overdue ? `Следующая — ${humanDateTime(next)}` : "Включён"}</span>
        ) : (
          <span className="chip chip--outline">Выключен</span>
        )
      }
    >
      <SetBlock>
        <form className="set-form" onSubmit={submit} noValidate>
          <label className="switch">
            <input type="checkbox" checked={v.auto_enabled} onChange={(e) => d.set("auto_enabled", e.target.checked)} />
            <span className="switch__track" aria-hidden="true" />
            <span>Подбирать посты по расписанию</span>
          </label>
          <fieldset className="set-fieldset" disabled={!v.auto_enabled}>
            <legend className="sr-only">Расписание подборки</legend>
            <div className="set-grid set-grid--3">
              <SetField id="auto-interval" label="Интервал, дней">
                <input
                  id="auto-interval"
                  className="input"
                  type="number"
                  min={1}
                  max={30}
                  step={1}
                  inputMode="numeric"
                  value={v.auto_interval_days}
                  onChange={(e) => d.set("auto_interval_days", e.target.value)}
                />
              </SetField>
              <SetField id="auto-hour" label="Час запуска">
                <select id="auto-hour" className="select" value={v.auto_hour} onChange={(e) => d.set("auto_hour", e.target.value)}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={String(h)}>
                      {hourLabel(h)}
                    </option>
                  ))}
                </select>
              </SetField>
              <SetField id="auto-tz" label="Часовой пояс">
                <input
                  id="auto-tz"
                  className="input"
                  list="auto-tz-list"
                  value={v.timezone}
                  onChange={(e) => d.set("timezone", e.target.value)}
                  placeholder="Asia/Yekaterinburg"
                  spellCheck={false}
                  autoComplete="off"
                />
                <datalist id="auto-tz-list">
                  {TZ_HINTS.map((tz) => (
                    <option key={tz} value={tz} />
                  ))}
                </datalist>
              </SetField>
              <SetField id="auto-news" label="Новостей за запуск">
                <input
                  id="auto-news"
                  className="input"
                  type="number"
                  min={0}
                  max={5}
                  step={1}
                  inputMode="numeric"
                  value={v.auto_news_count}
                  onChange={(e) => d.set("auto_news_count", e.target.value)}
                />
              </SetField>
              <SetField id="auto-facts" label="Фактов за запуск">
                <input
                  id="auto-facts"
                  className="input"
                  type="number"
                  min={0}
                  max={5}
                  step={1}
                  inputMode="numeric"
                  value={v.auto_fact_count}
                  onChange={(e) => d.set("auto_fact_count", e.target.value)}
                />
              </SetField>
              <SetField id="auto-myths" label="Мифов за запуск">
                <input
                  id="auto-myths"
                  className="input"
                  type="number"
                  min={0}
                  max={5}
                  step={1}
                  inputMode="numeric"
                  value={v.auto_myth_count}
                  onChange={(e) => d.set("auto_myth_count", e.target.value)}
                />
              </SetField>
            </div>
          </fieldset>

          <SetSaveBar
            dirty={d.dirty}
            busy={busy}
            error={error}
            onReset={() => {
              d.reset();
              setError("");
            }}
          />
        </form>
      </SetBlock>
    </SetSection>
  );
}

/* ================= Claude ================= */
/** Список моделей — из подписки (Agent SDK), сервер держит его сутки и обновляет при открытии Настроек. */
export function SettingsClaude({ s }: { s: PublicSettings }) {
  const toast = useToast();
  const qc = useQueryClient();
  const locked = s.env_locked.includes("claude_model");
  const models = useQuery({
    queryKey: ["claude-models"],
    queryFn: () => api.get<ClaudeModels>("/api/claude/models"),
    staleTime: 0,
    retry: false,
  });
  const list = models.data?.models ?? [];
  const selected = models.data?.selected ?? s.claude_model;
  // null — выбор не меняли
  const [choice, setChoice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const value = choice ?? selected;
  const dirty = choice !== null && choice !== selected;
  useSetDirty("claude", dirty);
  const check = useServiceCheck<ClaudeCheck>("/api/claude/check");
  const labelOf = (id: string) => list.find((m) => m.value === id)?.label ?? id;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (locked || !dirty || !choice) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.put<PublicSettings>("/api/settings", { claude_model: choice });
      qc.setQueryData(["settings"], r);
      qc.setQueryData<ClaudeModels>(["claude-models"], (m) => (m ? { ...m, selected: choice } : m));
      setChoice(null);
      toast(`Модель Claude: ${labelOf(choice)}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const res = check.result;
  const keySource = (src: string | null) =>
    src === "none" ? "подписка" : src ? `${src}: ожидалась подписка` : s.llm_provider === "mock" ? "тестовый режим" : "не сообщается";
  const hint = locked ? setEnvHint("claude_model") : models.data?.error ? `Список с подписки не обновился: ${models.data.error}` : null;

  return (
    <SetSection
      id="claude"
      title="Claude"
      status={
        s.llm_provider === "mock" ? (
          <span className="chip chip--outline">Тестовый режим</span>
        ) : (
          <span className="chip chip--ok">Подписка</span>
        )
      }
    >
      <SetBlock>
        <form className="set-form" onSubmit={submit} noValidate>
          <SetField id="claude-model" label="Модель" hint={hint}>
            <select
              id="claude-model"
              className="select set-input--mid"
              value={value}
              onChange={(e) => setChoice(e.target.value)}
              disabled={locked || models.isLoading}
              aria-describedby={hint ? setHintId("claude-model") : undefined}
            >
              {list.length === 0 && <option value={value}>{models.isLoading ? "Загружаю список…" : value}</option>}
              {list.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </SetField>
          {!locked && (
            <SetSaveBar
              dirty={dirty}
              busy={busy}
              error={error}
              onReset={() => {
                setChoice(null);
                setError("");
              }}
            />
          )}
        </form>
      </SetBlock>

      <SetBlock title="Проверка связи">
        <div className="set-actions">
          <button type="button" className="btn btn--dark" onClick={() => void check.run()} disabled={check.running || dirty}>
            <PlugZap size={17} aria-hidden="true" />
            Проверить
          </button>
          {dirty && <span className="set-save__note">Сначала сохраните</span>}
        </div>
        {check.running && (
          <div className="set-live">
            <EcgLoader label="Claude отвечает" />
            <span className="set-live__time">{duration(check.elapsed)}</span>
          </div>
        )}
        {check.error && (
          <div className="error-line" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            <span>{check.error}</span>
          </div>
        )}
        {res && (
          <div className={`set-check set-check--${res.ok ? (res.message ? "warn" : "ok") : "bad"}`} role="status">
            <p className="set-check__title">
              {res.ok ? (
                res.message ? (
                  <TriangleAlert size={18} aria-hidden="true" />
                ) : (
                  <CircleCheck size={18} aria-hidden="true" />
                )
              ) : (
                <CircleAlert size={18} aria-hidden="true" />
              )}
              {res.ok ? `Связь есть, ответ за ${duration(res.duration_ms)}` : `Claude не ответил (${duration(res.duration_ms)})`}
            </p>
            <SetFacts
              items={[
                ["Модель", res.model ?? labelOf(selected)],
                ["Вход", keySource(res.api_key_source)],
              ]}
            />
            {res.reply && <blockquote className="set-quote">{res.reply}</blockquote>}
            {res.message && <p className="set-check__msg">{res.message}</p>}
          </div>
        )}
      </SetBlock>
    </SetSection>
  );
}
